import React from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

interface Activity {
  name: string;
  type: string;
  details?: string;
  state?: string;
  assets?: {
    large_image?: string;
    large_text?: string;
  };
  timestamps?: {
    start?: number;
    end?: number;
  };
}

interface ActivityCarouselProps {
  activities: Activity[];
  defaultImage?: string;
}

const ActivityCarousel: React.FC<ActivityCarouselProps> = ({ activities, defaultImage = '/assets/images/unknown.png' }) => {
  const formatTime = (timestamp: number | undefined) => {
    if (!timestamp || isNaN(timestamp)) return '';
    const duration = Math.max(0, Date.now() - timestamp);
    const hours = Math.floor(duration / 3600000);
    const minutes = Math.floor((duration % 3600000) / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} elapsed`;
  };

  const filteredActivities = activities.filter(activity => activity.type !== 'CUSTOM');

  if (filteredActivities.length === 0) {
    return null; // Don't render the carousel if there are no activities
  }

  return (
    <div className="relative">
      <Carousel
        showArrows={false}
        showStatus={false}
        showThumbs={false}
        infiniteLoop
        autoPlay
        interval={5000}
        className="w-full max-w-[38rem] rounded-xl"
        renderIndicator={(onClickHandler, isSelected, index, label) => {
          const defStyle = { backgroundColor: "white", cursor: "pointer", display: "inline-block", margin: "0 8px", width: "8px", height: "8px", borderRadius: "50%" };
          const style = isSelected
            ? { ...defStyle, backgroundColor: "gray" }
            : { ...defStyle };
          return (
            <span
              style={style}
              onClick={onClickHandler}
              onKeyDown={onClickHandler}
              key={index}
              role="button"
              tabIndex={0}
              aria-label={`${label} ${index + 1}`}
            />
          );
        }}
      >
        {filteredActivities.map((activity, index) => (
          <div key={index} className="flex sm:flex-row flex-col w-full max-w-[38rem] justify-center items-center sm:gap-4 gap-2 rounded-xl pb-8">
            <img
              draggable="false"
              src={activity.assets?.large_image || defaultImage}
              alt=""
              className="min-[400px]:h-24 min-[400px]:w-24 h-20 w-20 min-[400px]:min-w-[6rem] min-[400px]:max-w-[6rem] min-w-[5rem] max-w-[5rem] select-none rounded-xl"
              style={{
                borderColor: 'rgb(255, 255, 255)',
                borderWidth: '2px',
              }}
            />
            <div className="flex flex-col text-left select-none">
              <h2 className="font-semibold whitespace-normal break-all text-white">{activity.name}</h2>
              {activity.details && <p className="whitespace-normal text-sm break-all text-white">{activity.details}</p>}
              {activity.state && <p className="whitespace-normal text-sm break-all text-white">{activity.state}</p>}
              {activity.timestamps?.start && (
                <div className="whitespace-normal text-sm font-medium text-white">
                  {formatTime(activity.timestamps.start)}
                </div>
              )}
            </div>
          </div>
        ))}
      </Carousel>
    </div>
  );
};

export default ActivityCarousel;